import { Box, Tooltip, Typography } from '@mui/material';
import React, { useId } from 'react';

// 'no-goal' = outside the flight; 'in-progress' = today, still under way and below goal.
// Both render neutral instead of red (ADOPS-3046).
export type PacingPointState = 'normal' | 'in-progress' | 'no-goal';

export interface PacingSparklineProps {
  values: number[]; // ratio (1.0 = on pace for that day). Index 0 = oldest, last = today.
  impressionsPerDay?: number[]; // optional, same length as values
  states?: PacingPointState[]; // optional, same length as values; defaults to all 'normal'
  width?: number;
  height?: number;
}

function dayLabel(daysAgo: number): string {
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo} days ago`;
}

function formatRatio(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function formatImpressions(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return `${Math.round(n)}`;
}

// Continuous green → yellow → red by distance from 1.0 (on-goal). Symmetric:
// both over and under 1.0 redden — the right semantics for PACING, where
// overspending pace is as bad as underspending.
// Hue lerps from 120 (green) at d=0 through 60 (yellow) at d=0.25 to 0 (red) at d=0.5+.
// Lightness 42% reads well on white rows; ~72% reads well on dark tooltip backgrounds.
export function continuousColor(v: number, lightness = 42): string {
  const d = Math.min(Math.abs(v - 1), 0.5);
  const hue = 120 - (d / 0.5) * 120;
  return `hsl(${hue}, 65%, ${lightness}%)`;
}

// One-directional variant for KPI performance: at or above goal (v >= 1) is
// unambiguously good, so it pins to full green and NEVER reddens; only a
// shortfall below goal ramps green → yellow → red. The below-goal half uses the
// same scale as continuousColor (full red at 50% below goal), so under-goal KPIs
// look identical — the only change is that over-goal no longer turns red.
// v is value/goal (1.0 = exactly at goal).
export function kpiColor(v: number, lightness = 42): string {
  const shortfall = v >= 1 ? 0 : Math.min(1 - v, 0.5);
  const hue = 120 - (shortfall / 0.5) * 120;
  return `hsl(${hue}, 65%, ${lightness}%)`;
}

// Same lightness argument as the scales above, so row (42) and tooltip (72) match.
export function mutedColor(lightness = 42): string {
  return `hsl(0, 0%, ${lightness}%)`;
}

// At or above this share of goal, today keeps normal formatting — an over-goal today is a
// real overspend and must still flag.
export const IN_PROGRESS_GOAL_THRESHOLD = 0.9;

// A day with no budget row has its ratio forced to 0, so it must be told apart from a real
// 0% delivery. no-goal wins over in-progress.
export function pacingPointState(hasGoal: boolean, isToday: boolean, ratio: number): PacingPointState {
  if (!hasGoal) return 'no-goal';
  if (isToday && ratio < IN_PROGRESS_GOAL_THRESHOLD) return 'in-progress';
  return 'normal';
}

export interface SparklineSVGProps {
  values: number[];
  states?: PacingPointState[];
  width?: number;
  height?: number;
  showGoalLine?: boolean;
  showEndpointDot?: boolean;
  // Maps a value/goal ratio to a color string. Defaults to the symmetric pacing color;
  // KPI callers pass kpiColor so over-goal points stay green.
  colorFn?: (v: number) => string;
}

// Chart-only sparkline — no tooltip wrapper. Embed inside other tooltips if needed.
export function SparklineSVG({
  values,
  states,
  width = 84,
  height = 28,
  showGoalLine = true,
  showEndpointDot = true,
  colorFn = continuousColor,
}: SparklineSVGProps) {
  const uid = useId();
  if (!values.length) return null;

  // Muted points keep their true y — only the color claim is withdrawn.
  const colorAt = (i: number) => ((states?.[i] ?? 'normal') === 'normal' ? colorFn(values[i]) : mutedColor());

  const padY = 4;
  const max = Math.max(2, ...values);
  const min = 0;
  const range = max - min || 1;
  const innerH = height - padY * 2;

  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const yFor = (v: number) => padY + (1 - (v - min) / range) * innerH;
  const goalY = yFor(1);

  const last = values[values.length - 1];
  const lastX = (values.length - 1) * stepX;
  const lastY = yFor(last);

  const gradients: React.ReactElement[] = [];
  const segments: React.ReactElement[] = [];
  for (let i = 0; i < values.length - 1; i += 1) {
    const x1 = i * stepX;
    const y1 = yFor(values[i]);
    const x2 = (i + 1) * stepX;
    const y2 = yFor(values[i + 1]);
    const gradId = `spark-${uid}-${i}`;
    gradients.push(
      <linearGradient key={gradId} id={gradId} gradientUnits="userSpaceOnUse" x1={x1} y1={y1} x2={x2} y2={y2}>
        <stop offset="0%" stopColor={colorAt(i)} />
        <stop offset="100%" stopColor={colorAt(i + 1)} />
      </linearGradient>
    );
    segments.push(
      <line
        key={`seg-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={`url(#${gradId})`}
        strokeWidth={1.5}
        strokeLinecap="round"
      />
    );
  }

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <defs>{gradients}</defs>
      {showGoalLine && (
        <line x1={0} y1={goalY} x2={width} y2={goalY} stroke="rgba(0,0,0,0.3)" strokeDasharray="3,2" strokeWidth={1} />
      )}
      {segments}
      {showEndpointDot && (
        <circle
          data-testid="spark-endpoint"
          data-state={states?.[values.length - 1] ?? 'normal'}
          cx={lastX}
          cy={lastY}
          r={2.5}
          fill={colorAt(values.length - 1)}
        />
      )}
    </svg>
  );
}

export interface PacingTooltipBodyProps {
  values: number[];
  impressionsPerDay?: number[];
  states?: PacingPointState[];
}

// Split out from the Tooltip wrapper so it can be rendered without simulating a hover.
export function PacingTooltipBody({ values, impressionsPerDay, states }: PacingTooltipBodyProps) {
  const reversedValues = [...values].reverse();
  const reversedImps = impressionsPerDay ? [...impressionsPerDay].reverse() : null;
  const reversedStates = states ? [...states].reverse() : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.25, minWidth: 180 }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, mb: 0.25, opacity: 0.85 }}>Daily pacing</Typography>
      {reversedValues.map((v, idx) => {
        const state: PacingPointState = reversedStates?.[idx] ?? 'normal';
        const color = state === 'normal' ? continuousColor(v, 72) : mutedColor(72);
        // No goal to be a percentage of, and "0%" would read as a total miss.
        const ratioLabel = state === 'no-goal' ? '—' : formatRatio(v);
        return (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            data-testid={`pacing-tooltip-row-${state}`}
            sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'baseline', columnGap: 1.5 }}
          >
            <span style={{ fontSize: '0.7rem', opacity: 0.85 }}>{dayLabel(idx)}</span>
            {reversedImps ? (
              <span style={{ fontSize: '0.7rem', opacity: 0.75, textAlign: 'right' }}>
                {formatImpressions(reversedImps[idx])} imps
              </span>
            ) : (
              <span />
            )}
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color, textAlign: 'right', minWidth: 36 }}>
              {ratioLabel}
            </span>
          </Box>
        );
      })}
    </Box>
  );
}

function PacingSparkline({ values, impressionsPerDay, states, width = 84, height = 28 }: PacingSparklineProps) {
  if (!values.length) return null;

  return (
    <Tooltip
      title={<PacingTooltipBody values={values} impressionsPerDay={impressionsPerDay} states={states} />}
      arrow
      placement="top"
    >
      <Box sx={{ display: 'inline-block', cursor: 'help' }}>
        <SparklineSVG values={values} states={states} width={width} height={height} />
      </Box>
    </Tooltip>
  );
}

export default PacingSparkline;
